"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SiteStorage = void 0;
function delay(time) {
    return new Promise(function (resolve) {
        setTimeout(resolve, time);
    });
}
var SiteStorage = /** @class */ (function () {
    function SiteStorage(_a) {
        var prefix = _a.prefix, _b = _a.separator, separator = _b === void 0 ? '::' : _b, _c = _a.WixSdk, WixSdk = _c === void 0 ? require('@wix/js-sdk-wrapper').WixSdk : _c, data = _a.data, style = _a.style;
        this.cache = {};
        this.cacheBool = {};
        this.cacheNumbers = {};
        this.prefix = prefix;
        this.separator = separator;
        this.WixSdk = WixSdk;
        this.data = data;
        this.style = style;
    }
    SiteStorage.prototype._joinWithSeparator = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return args.join(this.separator);
    };
    SiteStorage.prototype._getPrefixedKey = function (key) {
        return this._joinWithSeparator(this.prefix, key);
    };
    SiteStorage.prototype.getBool = function (key) {
        var prefix = this._getPrefixedKey(key);
        if (prefix in this.cacheBool) {
            // setEditorParam does not change params after they were retrieved
            return Promise.resolve(this.cacheBool[prefix]);
        }
        return this.__getStyleParams().then(function (params) {
            return params.booleans[prefix];
        });
    };
    SiteStorage.prototype.setBool = function (key, value) {
        var _this = this;
        var prefix = this._getPrefixedKey(key);
        this.cacheBool[prefix] = value;
        return this.__getStyleParams().then(function () { return _this.WixSdk.Styles.setBooleanParam(prefix, { value: value }); });
    };
    SiteStorage.prototype.getNumber = function (key) {
        var prefix = this._getPrefixedKey(key);
        if (prefix in this.cacheNumbers) {
            // setEditorParam does not change params after they were retrieved
            return Promise.resolve(this.cacheNumbers[prefix]);
        }
        return this.__getStyleParams().then(function (params) {
            return params.numbers[prefix];
        });
    };
    SiteStorage.prototype.setNumber = function (key, value) {
        var _this = this;
        var prefix = this._getPrefixedKey(key);
        this.cacheNumbers[prefix] = value;
        return this.__getStyleParams().then(function () { return _this.WixSdk.Styles.setNumberParam(prefix, { value: value }); });
    };
    /**
     * @name WMP.SiteStorage#get
     * @description Get value by key from wix site
     *
     * @pagit pram {string} key Name of the property
     * @returns {Promise} Value of the property (string|null)
     * @param {object} [options] Options of settings.
     * @param {Boolean} [options.skipData=false] Whether to skip getting from Wix.Data.
     */
    SiteStorage.prototype.get = function (key, options) {
        if (options === void 0) { options = {}; }
        var prefixedKey = this._getPrefixedKey(key);
        if (prefixedKey in this.cache) {
            // setEditorParam does not change params after they were retrieved
            return Promise.resolve(this.cache[prefixedKey]);
        }
        var idFromDataPromise = options.skipData ? Promise.resolve() : this.getDataPublic(key);
        var idFromStyleParamsPromise = this.__getStyleParams().then(function (styleParams) {
            return styleParams.fonts[prefixedKey] && styleParams.fonts[prefixedKey].value;
        });
        return idFromDataPromise.then(function (id) {
            return id || idFromStyleParamsPromise;
        });
    };
    /**
     * @name WMP.SiteStorage#remove
     * @description Removes value by key from wix site
     *
     * @param {string} key Name of the property
     * @returns {Promise}
     */
    SiteStorage.prototype.remove = function (key) {
        var _this = this;
        var prefixedKey = this._getPrefixedKey(key);
        var cache = this.cache;
        return this.__getStyleParams().then(function () {
            var resetFontParams = _this.WixSdk.Styles.setFontParam(prefixedKey, {
                value: { value: undefined },
            });
            var removeDataParam = _this.WixSdk.Data.Public.remove(prefixedKey, {
                scope: _this.WixSdk.Data.SCOPE.COMPONENT,
            }).then(function () {
                delete cache[prefixedKey];
            });
            var removeDataParamTimeout = Promise.race([removeDataParam, delay(3500)]);
            return Promise.all([resetFontParams, removeDataParamTimeout]);
        });
    };
    /**
     * @ngdoc method
     * @name wix.siteStorage#set
     * @description Set property by key
     *
     * @param {string} key Name of the property
     * @param {string} value Value of the property.
     * @param {object} [options] Options of settings.
     * @param {Boolean} [options.skipData = false] Whether to skip saving to Wix.Data.
     */
    SiteStorage.prototype.set = function (key, value, options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        if (value === undefined) {
            return Promise.resolve();
        }
        var prefixedKey = this._getPrefixedKey(key);
        this.cache[prefixedKey] = value;
        return this.__getStyleParams().then(function () {
            var setFontParams = _this.WixSdk.Styles.setFontParam(prefixedKey, {
                value: { value: value },
            });
            var setDataPublic = options.skipData ? Promise.resolve() : _this.setDataPublic(key, value);
            return Promise.all([setFontParams, Promise.race([setDataPublic, delay(1500)])]);
        });
    };
    SiteStorage.prototype.setDataPublic = function (key, value) {
        var _this = this;
        var prefixedKey = this._getPrefixedKey(key);
        return this.WixSdk.Data.Public.set(prefixedKey, value, {
            scope: this.WixSdk.Data.SCOPE.COMPONENT,
        }).then(function (data) {
            _this.WixSdk.Settings.triggerSettingsUpdatedEvent('data:update', data);
        });
    };
    SiteStorage.prototype.getDataPublic = function (key) {
        var prefixedKey = this._getPrefixedKey(key);
        return this.__getDataPublic(prefixedKey);
    };
    SiteStorage.prototype.__getStyleParams = function () {
        if (this.style) {
            return Promise.resolve(this.style);
        }
        return this.WixSdk.Styles.getStyleParams();
    };
    SiteStorage.prototype.__getDataPublic = function (key) {
        if (this.data) {
            return Promise.resolve(this.data[key]);
        }
        return this.WixSdk.Data.Public.get(key, {
            scope: this.WixSdk.Data.SCOPE.COMPONENT,
        })
            .then(function (data) { return data && data[key]; })
            .catch(function () { return undefined; });
    };
    return SiteStorage;
}());
exports.SiteStorage = SiteStorage;
//# sourceMappingURL=site-storage.js.map